/**
 * Master Ingredients Controller Module
 * Handles loading pure HTML partial templates, master ingredient table rendering, column sorting, schema validation, inspector modal, and editor modal.
 */

import { buildIngredientPhotoPrompt } from "./ingredientPhotoStyles.js";
import { refuseCatalogWrite } from "./catalogGuard.js";
import { BADGE, BUTTON, FIELD } from "./ui/classes.js";
import { icon, solidIcon } from "./ui/icons.js";
import { jsArg } from "./ui/copy.js";
import { showDialog, hideDialog } from "./ui/dialog.js";
import {
  parseAndNormalizeIngredientsJson as parseIngredientsJson,
  summarizeCatalogChange,
  estimatedDocumentBytes,
  MAX_CATALOG_BYTES
} from "./ingredientImport.js";

const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

let ingredientSortCol = "name";
let ingredientSortDir = "asc";

export const ALLOWED_UNITS = [
  "gram", "grams", "kg", "kilogram", "ml", "milliliter", "l", "liter",
  "oz", "ounce", "lb", "pound", "fl oz", "fluid ounce", "cup", "cups",
  "tbsp", "tablespoon", "tsp", "teaspoon", "clove", "cloves", "can", "cans",
  "count", "piece", "pieces", "pinch", "head", "bunch", "slice", "slices", "dash",
  "pkg", "package", "block", "bulb", "fillet", "steak", "link", "patty", "sheet", "scoop", "sprig", "stalk",
  // In use by existing master ingredients (bag alone is on 14) but missing from this list until now.
  "bag", "whole", "heart", "naan", "ear", "bun", "pouch", "pepper", "ball", "crown", "spear"
];

export const UNIT_PLURALS = {
  clove: "cloves",
  cup: "cups",
  tbsp: "tbsp",
  tsp: "tsp",
  count: "count",
  g: "g",
  gram: "grams",
  grams: "grams",
  kg: "kg",
  oz: "oz",
  ounce: "ounces",
  ml: "ml",
  bunch: "bunches",
  slice: "slices",
  whole: "whole",
  can: "cans",
  fillet: "fillets",
  package: "packages",
  pkg: "pkgs",
  head: "heads",
  stalk: "stalks",
  link: "links",
  block: "blocks",
  sprig: "sprigs",
  bun: "buns",
  bag: "bags",
  bulb: "bulbs",
  naan: "naan",
  steak: "steaks",
  sheet: "sheets",
  pepper: "peppers",
  heart: "hearts",
  scoop: "scoops",
  patty: "patties",
  pinch: "pinches",
  spear: "spears",
  pouch: "pouches",
  ear: "ears",
  ball: "balls",
  crown: "crowns",
  chopped: "chopped",
  dash: "dashes"
};

/**
 * The perishable checkbox has three states: checked (true), unchecked (false) and indeterminate (unknown, the field is
 * left off the ingredient). Clicking it clears the indeterminate state, so an ingredient only gets a value when someone
 * chooses one; saving an untouched ingredient never turns "unknown" into "false".
 */
export function setPerishableCheckbox(value) {
  const box = document.getElementById("formIngIsPerishable");
  if (!box) return;
  box.indeterminate = typeof value !== "boolean";
  box.checked = value === true;
}

/**
 * Retail pack size is optional: an empty box means "not set" and the field is left off the ingredient. A value that is not
 * a positive number is treated as not set too, so a typo can never save a zero or negative pack.
 */
export function setPackSizeInput(value) {
  const input = document.getElementById("formIngPackSize");
  if (input) input.value = typeof value === "number" ? String(value) : "";
}

export function readPackSizeInput() {
  const input = document.getElementById("formIngPackSize");
  if (!input) return undefined;
  const value = Number(input.value);
  return input.value.trim() !== "" && Number.isFinite(value) && value > 0 ? value : undefined;
}

export function readPerishableCheckbox() {
  const box = document.getElementById("formIngIsPerishable");
  if (!box || box.indeterminate) return undefined;
  return box.checked;
}

export const PRICE_TIERS = ["$", "$$", "$$$", "$$$$"];

/**
 * Price tier is optional, same as pack_size: the "Not set" option means the ingredient is left off the cost gauge
 * (#482) rather than treated as free. Only a value from PRICE_TIERS is ever stored.
 */
export function setPriceTierSelect(value) {
  const select = document.getElementById("formIngPriceTier");
  if (select) select.value = PRICE_TIERS.includes(value) ? value : "";
}

export function readPriceTierSelect() {
  const select = document.getElementById("formIngPriceTier");
  if (!select) return undefined;
  return PRICE_TIERS.includes(select.value) ? select.value : undefined;
}

export function getPluralUnit(unit) {
  if (!unit) return "";
  const lower = unit.toLowerCase().trim();
  if (UNIT_PLURALS[lower]) return UNIT_PLURALS[lower];
  if (lower.endsWith("s")) return lower;
  return lower + "s";
}

export async function loadIngredientsTemplate() {
  const container = document.getElementById("sectionIngredientsContainer");
  if (!container) return;

  if (document.getElementById("sectionIngredients")) return;

  try {
    const resp = await fetch("templates/ingredients.html");
    if (!resp.ok) {
      throw new Error(`Failed to load ingredients template: ${resp.status} ${resp.statusText}`);
    }
    const html = await resp.text();
    container.innerHTML = html;
  } catch (err) {
    console.error("❌ Ingredients Template Load Error:", err);
    container.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Could not load the ingredients template: ${escapeHtml(err.message)}</div>`;
  }
}

export function validateIngredient(key, item, masterMap = {}, categoriesDoc = {}) {
  const issues = [];
  const snakeCaseRegex = /^[a-z0-9]+(_[a-z0-9]+)*$/;

  // Rule 1: Key format must be lowercase snake_case
  if (!snakeCaseRegex.test(key)) {
    issues.push(`ID key '${key}' must be lowercase snake_case`);
  }

  // Rule 2: Canonical unit must be strictly 'gram' or 'ml'
  const canonicalUnit = (item.canonical_unit || "").toLowerCase();
  if (canonicalUnit !== "gram" && canonicalUnit !== "ml") {
    issues.push(`Canonical unit '${canonicalUnit}' must be strictly 'gram' or 'ml'`);
  }

  // Rule 3: Display unit validation
  const displayUnit = (item.display_unit || "").toLowerCase();
  if (!displayUnit) {
    issues.push("Display unit is required");
  } else if (!ALLOWED_UNITS.includes(displayUnit)) {
    issues.push(`Display unit '${displayUnit}' is not in allowed schema units`);
  }

  // Rule 4: Canonical quantity validation
  const canonicalQuantity = typeof item.canonical_quantity === "number" ? item.canonical_quantity : Number(item.canonical_quantity);
  if (isNaN(canonicalQuantity) || canonicalQuantity <= 0) {
    issues.push(`Canonical quantity must be a positive number (found: ${item.canonical_quantity})`);
  }

  // Rule 4b: is_perishable is optional, but when present it must be a boolean (missing means unknown)
  if (item.is_perishable !== undefined && typeof item.is_perishable !== "boolean") {
    issues.push(`is_perishable must be true or false (found: ${JSON.stringify(item.is_perishable)})`);
  }

  // Rule 4c: pack_size is optional (grams or ml in one retail pack); when present it must be a positive number
  if (item.pack_size !== undefined && (typeof item.pack_size !== "number" || !Number.isFinite(item.pack_size) || item.pack_size <= 0)) {
    issues.push(`pack_size must be a positive number (found: ${JSON.stringify(item.pack_size)})`);
  }

  // Rule 4d: price_tier is optional (#482 cost gauge); when present it must be one of the four bands
  if (item.price_tier !== undefined && !PRICE_TIERS.includes(item.price_tier)) {
    issues.push(`price_tier must be one of ${PRICE_TIERS.join(", ")} (found: ${JSON.stringify(item.price_tier)})`);
  }

  // Rule 5: Subgroup code validation
  if (!item.subgroup_code) {
    issues.push("Supermarket aisle subgroup code is required");
  }

  // Rule 3: Substitute references must exist in masterIngredients
  if (item.substitutes && Array.isArray(item.substitutes)) {
    item.substitutes.forEach((sub) => {
      if (sub.uid && masterMap && !masterMap[sub.uid]) {
        issues.push(`Substitute ingredient '${sub.uid}' does not exist in master ingredients list`);
      }
    });
  }

  // Rule 4: Allergens & Dietary Preference IDs must exist in categories document
  const registeredAllergenIds = (categoriesDoc.allergens || []).map((a) => a.id);
  const registeredDietaryIds = (categoriesDoc.dietary_preferences || []).map((d) => d.id);

  const itemAllergens = item.allergen_flags || item.allergens || [];
  itemAllergens.forEach((algId) => {
    if (registeredAllergenIds.length > 0 && !registeredAllergenIds.includes(algId)) {
      issues.push(`Allergen flag '${algId}' is not registered in categories document`);
    }
  });

  const itemDiets = item.dietary_preferences || [];
  itemDiets.forEach((dietId) => {
    if (registeredDietaryIds.length > 0 && !registeredDietaryIds.includes(dietId)) {
      issues.push(`Dietary preference '${dietId}' is not registered in categories document`);
    }
  });

  return issues;
}

export function getAllergenDisplayName(algId, categoriesData = {}) {
  const list = categoriesData.allergens || [];
  const found = list.find((a) => a.id === algId);
  if (found) {
    const emoji = found.emoji ? `${found.emoji} ` : "";
    return `${emoji}${found.name || algId}`;
  }
  return algId;
}

export function getDietaryDisplayName(dietId, categoriesData = {}) {
  const list = categoriesData.dietary_preferences || [];
  const found = list.find((d) => d.id === dietId);
  if (found) {
    const emoji = found.emoji ? `${found.emoji} ` : "";
    return `${emoji}${found.name || dietId}`;
  }
  return dietId;
}

export function setIngredientSort(col, renderFn) {
  if (ingredientSortCol === col) {
    ingredientSortDir = ingredientSortDir === "asc" ? "desc" : "asc";
  } else {
    ingredientSortCol = col;
    ingredientSortDir = "asc";
  }
  updateSortIcons();
  if (renderFn) renderFn();
}

// The chevron points down for descending; data-active shows it, asc turns it over (as on Recipes).
function updateSortIcons() {
  document.querySelectorAll("[data-ingredient-sort]").forEach((el) => {
    const active = el.dataset.ingredientSort === ingredientSortCol;
    el.toggleAttribute("data-active", active);
    el.dataset.sortDir = active ? ingredientSortDir : "";
  });
}

export function onIngredientSearchInput() {
  renderIngredientsTable(activeMasterIngredients, activeCategoriesData);
}

export function renderIngredientsTable(masterIngredients = {}, categoriesData = {}) {
  const tbody = document.getElementById("ingredientTableBody");
  if (!tbody) return;

  const ingredientsMap = (masterIngredients && Object.keys(masterIngredients).length > 0)
    ? masterIngredients
    : activeMasterIngredients;
  const catData = (categoriesData && Object.keys(categoriesData).length > 0)
    ? categoriesData
    : activeCategoriesData;

  if (masterIngredients && Object.keys(masterIngredients).length > 0) {
    activeMasterIngredients = masterIngredients;
  }
  if (categoriesData && Object.keys(categoriesData).length > 0) {
    activeCategoriesData = categoriesData;
  }

  const searchVal = (document.getElementById("ingredientSearchInput")?.value || "").toLowerCase().trim();
  const categoryFilter = document.getElementById("ingredientCategoryFilter")?.value || "all";

  let keys = Object.keys(ingredientsMap).filter((key) => {
    const item = ingredientsMap[key] || {};
    const name = (item.display_name || item.name || key).toLowerCase();
    const category = (item.subgroup_code || item.category || "").toLowerCase();
    const matchesSearch = !searchVal || name.includes(searchVal) || key.toLowerCase().includes(searchVal);
    const matchesCategory = categoryFilter === "all" || category === categoryFilter.toLowerCase();
    return matchesSearch && matchesCategory;
  });

  // Sort logic
  keys.sort((keyA, keyB) => {
    const itemA = ingredientsMap[keyA] || {};
    const itemB = ingredientsMap[keyB] || {};
    let valA = "";
    let valB = "";

    if (ingredientSortCol === "name") {
      valA = (itemA.display_name || itemA.name || keyA).toLowerCase();
      valB = (itemB.display_name || itemB.name || keyB).toLowerCase();
    } else if (ingredientSortCol === "category") {
      valA = (itemA.subgroup_code || itemA.category || "").toLowerCase();
      valB = (itemB.subgroup_code || itemB.category || "").toLowerCase();
    } else if (ingredientSortCol === "unit") {
      valA = (itemA.display_unit || "").toLowerCase();
      valB = (itemB.display_unit || "").toLowerCase();
    } else if (ingredientSortCol === "tags" || ingredientSortCol === "allergens") {
      valA = (itemA.allergen_flags || itemA.allergens || []).length + (itemA.dietary_preferences || []).length;
      valB = (itemB.allergen_flags || itemB.allergens || []).length + (itemB.dietary_preferences || []).length;
    }

    if (valA < valB) return ingredientSortDir === "asc" ? -1 : 1;
    if (valA > valB) return ingredientSortDir === "asc" ? 1 : -1;
    return 0;
  });

  if (keys.length === 0) {
    tbody.innerHTML = `<tr><td colspan="5" class="px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">No master ingredients match.</td></tr>`;
    return;
  }

  tbody.innerHTML = keys.map((key) => {
    const item = ingredientsMap[key] || {};
    const displayName = escapeHtml(item.display_name || item.name || key);
    const category = escapeHtml(item.subgroup_name || item.subgroup_code || item.category || "General");
    const displayUnit = escapeHtml(item.display_unit || "gram");
    const canonicalUnit = escapeHtml(item.canonical_unit || "gram");
    const canonicalQuantity = typeof item.canonical_quantity === "number" ? item.canonical_quantity : 1;
    const safeKey = escapeHtml(key);

    const allergenBadges = (item.allergen_flags || item.allergens || [])
      .map((a) => `<span class="${BADGE.red}">${escapeHtml(getAllergenDisplayName(a, catData))}</span>`);
    const dietBadges = (item.dietary_preferences || [])
      .map((d) => `<span class="${BADGE.green}">${escapeHtml(getDietaryDisplayName(d, catData))}</span>`);
    const flagsHtml = [...allergenBadges, ...dietBadges].join("") || `<span class="text-sm text-gray-400 dark:text-gray-500">None</span>`;

    // Schema check: a green tick, or a warning whose tooltip lists the problems.
    const validationIssues = validateIngredient(key, item, ingredientsMap, catData);
    const validationHtml = validationIssues.length === 0
      ? `<span title="Matches the schema" class="flex-none">${solidIcon("check-circle", "size-5 text-green-500")}</span>`
      : `<span data-tooltip="${escapeHtml(`Schema problems:\n• ${validationIssues.join("\n• ")}`)}" class="flex-none">${solidIcon("exclamation-triangle", "size-5 text-yellow-500")}</span>`;

    const imagePath = item.image_path || item.imageUrl || item.image_url || "";
    const thumbUrl = imagePath ? (resolveIngredientThumbnailUrl(imagePath) || resolveIngredientImageUrl(imagePath)) : "";
    const photoHtml = thumbUrl
      ? `<img src="${escapeHtml(thumbUrl)}" alt="" loading="lazy" class="size-10 flex-none rounded-md bg-gray-100 object-cover outline -outline-offset-1 outline-black/5 dark:bg-gray-800 dark:outline-white/10">`
      : `<div title="No photo" class="flex size-10 flex-none items-center justify-center rounded-md bg-gray-100 text-gray-400 dark:bg-white/5 dark:text-gray-500">${icon("photo", "size-5")}</div>`;
    const perishableHtml = item.is_perishable === true ? `<span class="${BADGE.blue}">Perishable</span>` : "";

    return `
      <tr onclick="window.crmApp.openIngredientInspector(${jsArg(key)})" class="cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5">
        <td class="py-3 pr-3 pl-4 sm:pl-6">
          <div class="flex items-center gap-3">
            ${validationHtml}
            ${photoHtml}
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">${displayName}${perishableHtml}</div>
              <div class="font-mono text-xs text-gray-500 dark:text-gray-400">${safeKey}</div>
            </div>
          </div>
        </td>
        <td class="px-3 py-3 whitespace-nowrap"><span class="${BADGE.gray}">${category}</span></td>
        <td class="px-3 py-3 text-sm whitespace-nowrap"><span class="font-medium text-gray-900 dark:text-white">${displayUnit}</span> <span class="text-gray-500 dark:text-gray-400">(${canonicalQuantity} ${canonicalUnit})</span></td>
        <td class="px-3 py-3"><div class="flex max-w-72 flex-wrap gap-1">${flagsHtml}</div></td>
        <td class="py-3 pr-4 pl-3 text-right text-sm whitespace-nowrap sm:pr-6">
          <button type="button" onclick="event.stopPropagation(); window.crmApp.openIngredientInspector(${jsArg(key)})" class="${BUTTON.link}">Details<span class="sr-only">, ${displayName}</span></button>
        </td>
      </tr>
    `;
  }).join("");
}

export function openIngredientInspector(key, masterIngredients = {}, categoriesData = {}) {
  const item = masterIngredients[key];
  if (!item) return;

  if (!document.getElementById("ingredientInspectorModal")) return;

  document.getElementById("inspectorTitle").textContent = item.display_name || item.name || key;
  document.getElementById("inspectorUidText").textContent = key;

  // Photo Preview Card
  const photoCard = document.getElementById("inspectorPhotoCard");
  const photoImg = document.getElementById("inspectorPhotoImg");
  const photoPathEl = document.getElementById("inspectorPhotoPath");
  const imagePath = item.image_path || item.imageUrl || item.image_url || "";

  if (photoCard && photoImg) {
    if (imagePath) {
      const thumbUrl = resolveIngredientThumbnailUrl(imagePath) || resolveIngredientImageUrl(imagePath);
      photoImg.src = thumbUrl;
      photoImg.onerror = () => {
        photoCard.style.display = "none";
      };
      if (photoPathEl) photoPathEl.textContent = imagePath;
      photoCard.style.display = "flex";
    } else {
      photoImg.src = "";
      photoCard.style.display = "none";
    }
  }
  const displayUnit = item.display_unit || "gram";
  const pluralUnit = item.plural || getPluralUnit(displayUnit);
  const dispEl = document.getElementById("inspectorDisplayUnit");
  if (dispEl) {
    dispEl.innerHTML = `${escapeHtml(displayUnit)} <span id="inspectorPluralUnit" class="font-normal text-gray-500 dark:text-gray-400">(${escapeHtml(pluralUnit)})</span>`;
  }
  const canonUnitEl = document.getElementById("inspectorCanonicalUnit");
  if (canonUnitEl) canonUnitEl.textContent = item.canonical_unit || "gram";
  const canonQtyEl = document.getElementById("inspectorCanonicalQuantity");
  if (canonQtyEl) canonQtyEl.textContent = item.canonical_quantity != null ? String(item.canonical_quantity) : "1";
  const packSizeEl = document.getElementById("inspectorPackSize");
  if (packSizeEl) packSizeEl.textContent = typeof item.pack_size === "number" ? `${item.pack_size} ${item.canonical_unit || "gram"}` : "Not set";
  const priceTierEl = document.getElementById("inspectorPriceTier");
  if (priceTierEl) priceTierEl.textContent = PRICE_TIERS.includes(item.price_tier) ? item.price_tier : "Not set";
  const subNameEl = document.getElementById("inspectorSubgroupName");
  if (subNameEl) subNameEl.textContent = item.subgroup_name || (item.subgroup_code ? item.subgroup_code.replace(/_/g, " ") : "General");
  const subCodeEl = document.getElementById("inspectorSubgroupCode");
  if (subCodeEl) subCodeEl.textContent = item.subgroup_code || "general";

  // Yes / No / Unknown as badges: green for a yes, red for "never on a grocery list".
  const setFlag = (id, text, tone) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.className = BADGE[tone];
    el.textContent = text;
  };
  setFlag("inspectorPantryStaple", item.is_pantry_staple === true ? "Yes" : "No", item.is_pantry_staple === true ? "green" : "gray");
  const known = typeof item.is_perishable === "boolean";
  setFlag("inspectorPerishable", known ? (item.is_perishable ? "Yes" : "No") : "Unknown", known && item.is_perishable ? "green" : known ? "gray" : "yellow");
  const excluded = item.exclude_from_grocery_list === true;
  setFlag("inspectorExcludeFromGroceryList", excluded ? "Never" : "Shown", excluded ? "red" : "gray");

  // Allergens & Diets
  const allergens = item.allergen_flags || item.allergens || [];
  const diets = item.dietary_preferences || [];

  const none = `<span class="text-sm text-gray-400 dark:text-gray-500">None</span>`;
  document.getElementById("inspectorAllergensList").innerHTML = allergens.length
    ? allergens.map((a) => `<span class="${BADGE.red}">${escapeHtml(getAllergenDisplayName(a, categoriesData))}</span>`).join("")
    : none;
  document.getElementById("inspectorDietsList").innerHTML = diets.length
    ? diets.map((d) => `<span class="${BADGE.green}">${escapeHtml(getDietaryDisplayName(d, categoriesData))}</span>`).join("")
    : none;

  // Substitutes: a link to each one in the catalog; one missing from it opens a draft in the editor.
  const substitutes = item.substitutes || [];
  document.getElementById("inspectorSubstitutesList").innerHTML = substitutes.length
    ? substitutes.map((sub) => {
        const uid = escapeHtml(sub.uid);
        const reason = escapeHtml(normalizeSubstituteReason(sub.reason));
        if (masterIngredients[sub.uid]) {
          const name = escapeHtml(masterIngredients[sub.uid].display_name || sub.uid);
          return `
            <div class="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2 dark:bg-white/5">
              <button type="button" onclick="window.crmApp.openIngredientInspector(${jsArg(sub.uid)})" class="min-w-0 truncate text-left text-sm font-medium text-paprika-700 hover:text-paprika-800 dark:text-paprika-400 dark:hover:text-paprika-300">${name} <span class="font-mono text-xs font-normal text-gray-500 dark:text-gray-400">${uid}</span></button>
              <span class="${BADGE.gray}">${reason}</span>
            </div>`;
        }
        return `
          <div class="flex items-center justify-between gap-3 rounded-md bg-yellow-50 px-3 py-2 ring-1 ring-yellow-600/20 ring-inset dark:bg-yellow-500/10 dark:ring-yellow-400/20">
            <div class="flex min-w-0 items-center gap-2 text-sm">
              ${solidIcon("exclamation-triangle", "size-4 flex-none text-yellow-500")}
              <span class="truncate font-mono text-yellow-800 dark:text-yellow-200">${uid}</span>
              <span class="text-yellow-700 dark:text-yellow-300">is not in the catalog</span>
            </div>
            <button type="button" onclick="window.crmApp.openAddMissingIngredientForm(${jsArg(sub.uid)})" class="${BUTTON.small}">Open a draft</button>
          </div>`;
      }).join("")
    : `<p class="text-sm text-gray-500 italic dark:text-gray-400">No substitutes.</p>`;

  const editBtn = document.getElementById("inspectorEditBtn");
  if (editBtn) {
    editBtn.onclick = () => {
      hideDialog("ingredientInspectorModal");
      openIngredientEditor(key, masterIngredients, categoriesData);
    };
  }

  showDialog("ingredientInspectorModal");
}

export function populateCategoryDropdown(categoriesData = {}, currentSubgroupCode = null) {
  const select = document.getElementById("formIngCategory");
  if (!select) return;

  const subgroupOptions = [];
  const ingCategories = categoriesData.ingredient_categories || [];

  ingCategories.forEach((cat) => {
    const mainCategoryName = cat.name || cat.code || "Category";
    const subgroups = cat.subgroups || [];

    subgroups.forEach((sub) => {
      const code = typeof sub === "string" ? sub : sub.code || sub.id;
      const name = typeof sub === "string" ? sub : sub.name || sub.display_name || code;
      if (code && !subgroupOptions.some((opt) => opt.code === code)) {
        subgroupOptions.push({
          code,
          name,
          categoryName: mainCategoryName
        });
      }
    });
  });

  // Preserve existing subgroup code if not currently in ingredient_categories schema
  if (currentSubgroupCode && !subgroupOptions.some((opt) => opt.code === currentSubgroupCode)) {
    subgroupOptions.push({
      code: currentSubgroupCode,
      name: currentSubgroupCode.replace(/_/g, " "),
      categoryName: "Custom / Uncategorized"
    });
  }

  // Group options by Main Supermarket Category using <optgroup>
  const grouped = {};
  subgroupOptions.forEach((opt) => {
    grouped[opt.categoryName] = grouped[opt.categoryName] || [];
    grouped[opt.categoryName].push(opt);
  });

  let html = `<option value="">Choose an aisle</option>`;
  Object.keys(grouped).forEach((groupName) => {
    html += `<optgroup label="${escapeHtml(groupName)}">`;
    grouped[groupName].forEach((opt) => {
      html += `<option value="${escapeHtml(opt.code)}">${escapeHtml(opt.name)}</option>`;
    });
    html += `</optgroup>`;
  });

  select.innerHTML = html;
}

let activeEditorAllergens = [];
let activeEditorDiets = [];
let activeEditorSubstitutes = [];
let activeCategoriesData = {};
let activeMasterIngredients = {};

const DEFAULT_MASTER_INGREDIENTS_PREFIX = "https://res.cloudinary.com/psttnxd0/image/upload/f_auto,q_auto";
let activeMasterIngredientsPrefix = DEFAULT_MASTER_INGREDIENTS_PREFIX;

export function updateActiveCategoriesData(categoriesData = {}) {
  activeCategoriesData = categoriesData;
}

export function updateActiveMasterIngredients(masterIngredients = {}) {
  activeMasterIngredients = masterIngredients;
}

export function updateActiveMasterIngredientsPrefix(prefix = "") {
  if (prefix && typeof prefix === "string") {
    activeMasterIngredientsPrefix = prefix.trim();
  }
}

export function resolveIngredientImageUrl(imagePath, prefix = null) {
  if (!imagePath || typeof imagePath !== "string") return null;
  const path = imagePath.trim();
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const cleanPrefix = (prefix || activeMasterIngredientsPrefix || "https://res.cloudinary.com/psttnxd0/image/upload/f_auto,q_auto").replace(/\/+$/, "");
  const cleanPath = path.replace(/^\/+/, "");
  return `${cleanPrefix}/${cleanPath}`;
}

export function resolveIngredientThumbnailUrl(imagePath) {
  if (!imagePath || typeof imagePath !== "string") return null;
  const path = imagePath.trim();
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const cleanPath = path.replace(/^\/+/, "");
  return `https://res.cloudinary.com/psttnxd0/image/upload/c_fill,g_auto,w_400,h_400,f_auto,q_auto/${cleanPath}`;
}

export function onIngredientImagePathChanged(pathValue) {
  const previewImg = document.getElementById("formIngPhotoPreview");
  const placeholder = document.getElementById("formIngPhotoPlaceholder");
  const url = resolveIngredientThumbnailUrl(pathValue) || resolveIngredientImageUrl(pathValue);
  if (url) {
    if (previewImg) {
      previewImg.src = url;
      previewImg.style.display = "block";
      previewImg.onerror = () => {
        previewImg.style.display = "none";
        if (placeholder) placeholder.style.display = "block";
      };
    }
    if (placeholder) placeholder.style.display = "none";
  } else {
    if (previewImg) {
      previewImg.src = "";
      previewImg.style.display = "none";
    }
    if (placeholder) placeholder.style.display = "block";
  }
}

export function downloadMasterIngredientsJson(masterIngredients = null) {
  const data = (masterIngredients && Object.keys(masterIngredients).length > 0)
    ? masterIngredients
    : activeMasterIngredients;

  if (!data || Object.keys(data).length === 0) {
    alert("No ingredients data loaded to export.");
    return;
  }

  const jsonStr = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonStr], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const timestamp = new Date().toISOString().split("T")[0];
  a.href = url;
  a.download = `master_ingredients_${timestamp}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function openIngredientImportJsonModal() {
  const modal = document.getElementById("ingredientImportJsonModal");
  if (!modal) return;
  const textarea = document.getElementById("rawIngredientsJsonInput");
  if (textarea) textarea.value = "";
  const fileInput = document.getElementById("importJsonFileInput");
  if (fileInput) fileInput.value = "";
  const statusEl = document.getElementById("importJsonStatus");
  if (statusEl) statusEl.textContent = "Paste JSON or load a file to check it.";
  const applyBtn = document.getElementById("btnApplyJsonImport");
  if (applyBtn) applyBtn.disabled = true;
  showDialog("ingredientImportJsonModal");
}

function parseAndNormalizeIngredientsJson(rawText) {
  return parseIngredientsJson(rawText, getPluralUnit);
}

export function onIngredientJsonInput() {
  const text = document.getElementById("rawIngredientsJsonInput")?.value || "";
  const statusEl = document.getElementById("importJsonStatus");
  const applyBtn = document.getElementById("btnApplyJsonImport");
  if (!statusEl || !applyBtn) return;

  if (!text.trim()) {
    statusEl.textContent = "Paste JSON or load a file to check it.";
    applyBtn.disabled = true;
    return;
  }

  const res = parseAndNormalizeIngredientsJson(text);
  if (res.error) {
    statusEl.innerHTML = `<span class="font-medium text-red-600 dark:text-red-400">${escapeHtml(res.error)}</span>`;
    applyBtn.disabled = true;
  } else {
    const prefixNote = res.image_url_prefix
      ? `<br><span>Photo URL prefix from the file: <code class="font-mono text-xs">${escapeHtml(res.image_url_prefix)}</code></span>`
      : "";
    const ignoredNote = res.ignoredFields && res.ignoredFields.length
      ? `<br><span class="font-medium text-amber-700 dark:text-amber-400">Ignored, not in the ingredient schema: ${res.ignoredFields.map(escapeHtml).join(", ")}</span>`
      : "";
    statusEl.innerHTML = `<span class="font-medium text-green-700 dark:text-green-400">Valid: <strong>${res.count}</strong> master ingredients.</span>${prefixNote}${ignoredNote}`;
    applyBtn.disabled = false;
  }
}

export function onIngredientJsonFileSelected(event) {
  const file = event?.target?.files?.[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    const content = e.target.result;
    const textarea = document.getElementById("rawIngredientsJsonInput");
    if (textarea) {
      textarea.value = content;
      onIngredientJsonInput();
    }
  };
  reader.readAsText(file);
}

export async function applyIngredientJsonImport(db, setDoc, doc, showToast, dismissToast, renderIngredientsTable) {
  if (refuseCatalogWrite(showToast)) return; // git owns the catalog (#1188)
  const text = document.getElementById("rawIngredientsJsonInput")?.value || "";
  const res = parseAndNormalizeIngredientsJson(text);

  if (res.error || !res.ingredients || res.count === 0) {
    if (showToast) showToast(res.error || "Invalid ingredients payload.", "danger");
    return;
  }

  const count = res.count;
  const prefix = res.image_url_prefix || activeMasterIngredientsPrefix || DEFAULT_MASTER_INGREDIENTS_PREFIX;
  const payload = {
    ingredients: res.ingredients,
    count: count,
    image_url_prefix: prefix,
    updated_at: new Date().toISOString()
  };

  if (estimatedDocumentBytes(payload) > MAX_CATALOG_BYTES) {
    if (showToast) showToast("❌ This catalog is too large for a single Firestore document (1 MiB limit). Nothing was changed.", "danger");
    return;
  }

  const change = summarizeCatalogChange(activeMasterIngredients, res.ingredients);
  const preview = (list) => list.slice(0, 8).join(", ") + (list.length > 8 ? `, … (+${list.length - 8} more)` : "");
  const lines = [
    `Replace the entire master ingredients catalog with ${count} ingredients?`,
    "",
    `• ${change.added.length} added${change.added.length ? `: ${preview(change.added)}` : ""}`,
    `• ${change.updated.length} changed${change.updated.length ? `: ${preview(change.updated)}` : ""}`,
    `• ${change.removed.length} REMOVED from Firestore${change.removed.length ? `: ${preview(change.removed)}` : ""}`,
    `• ${change.unchanged} unchanged`,
    "",
    `Photo URL prefix: ${prefix}${res.image_url_prefix ? " (from file)" : " (kept)"}`,
    "",
    "This overwrites 'globals/master_ingredients'."
  ];
  const confirmed = confirm(lines.join("\n"));
  if (!confirmed) return;

  const applyBtn = document.getElementById("btnApplyJsonImport");
  if (applyBtn) {
    applyBtn.disabled = true;
    applyBtn.textContent = `Uploading ${count} ingredients…`;
  }

  const processingToast = showToast ? showToast(`⏳ Overwriting catalog with ${count} ingredients in Firestore...`, "processing") : null;

  try {
    // No `merge`: merging deep-merges the `ingredients` map, which keeps every ingredient (and field) that is missing from
    // the upload, so a "replace" would silently leave the old data in place.
    await setDoc(doc(db, "globals", "master_ingredients"), payload);

    activeMasterIngredients = res.ingredients;
    activeMasterIngredientsPrefix = prefix;

    if (dismissToast && processingToast) dismissToast(processingToast);
    hideDialog("ingredientImportJsonModal");
    if (renderIngredientsTable) renderIngredientsTable();
    if (showToast) showToast(`✅ Successfully updated catalog with ${count} master ingredients!`, "success");
  } catch (err) {
    if (dismissToast && processingToast) dismissToast(processingToast);
    console.error("❌ Failed to import ingredients catalog JSON:", err);
    if (showToast) showToast(`❌ Import failed: ${err.message}`, "danger");
  } finally {
    if (applyBtn) {
      applyBtn.disabled = false;
      applyBtn.textContent = "Replace the entire catalog";
    }
  }
}

// Allergen and diet chips: toggle buttons, red or green while pressed.
const ALLERGEN_CHIP =
  "inline-flex items-center gap-x-1 rounded-full bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 ring-1 ring-gray-200 ring-inset hover:bg-gray-100 " +
  "aria-pressed:bg-red-600 aria-pressed:text-white aria-pressed:ring-red-600 dark:bg-white/5 dark:text-gray-300 dark:ring-white/10 dark:hover:bg-white/10 " +
  "dark:aria-pressed:bg-red-500 dark:aria-pressed:text-white dark:aria-pressed:ring-red-500";
const DIET_CHIP =
  "inline-flex items-center gap-x-1 rounded-full bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 ring-1 ring-gray-200 ring-inset hover:bg-gray-100 " +
  "aria-pressed:bg-green-700 aria-pressed:text-white aria-pressed:ring-green-700 dark:bg-white/5 dark:text-gray-300 dark:ring-white/10 dark:hover:bg-white/10 " +
  "dark:aria-pressed:bg-green-600 dark:aria-pressed:text-white dark:aria-pressed:ring-green-600";

function chipGridHtml(list, selected, chipClass, toggleFn) {
  return list.map((tag) => {
    const label = `${tag.emoji ? `${tag.emoji} ` : ""}${tag.name || tag.id}`;
    return `<button type="button" aria-pressed="${selected.includes(tag.id)}" onclick="window.crmApp.${toggleFn}(${jsArg(tag.id)})" class="${chipClass}">${escapeHtml(label)}</button>`;
  }).join("");
}

export function renderAllergenChipGrid() {
  const container = document.getElementById("formIngAllergensChipGrid");
  if (!container) return;
  const list = activeCategoriesData.allergens || [];
  container.innerHTML = list.length
    ? chipGridHtml(list, activeEditorAllergens, ALLERGEN_CHIP, "toggleIngredientAllergenTag")
    : `<span class="text-sm text-gray-500 italic dark:text-gray-400">No allergens in globals/categories.</span>`;
}

export function renderDietChipGrid() {
  const container = document.getElementById("formIngDietsChipGrid");
  if (!container) return;
  const list = activeCategoriesData.dietary_preferences || [];
  container.innerHTML = list.length
    ? chipGridHtml(list, activeEditorDiets, DIET_CHIP, "toggleIngredientDietTag")
    : `<span class="text-sm text-gray-500 italic dark:text-gray-400">No diets in globals/categories.</span>`;
}

export function toggleIngredientAllergenTag(tagId) {
  if (!tagId) return;
  if (activeEditorAllergens.includes(tagId)) {
    activeEditorAllergens = activeEditorAllergens.filter((t) => t !== tagId);
  } else {
    activeEditorAllergens.push(tagId);
  }
  renderAllergenChipGrid();
}

export function toggleIngredientDietTag(tagId) {
  if (!tagId) return;
  if (activeEditorDiets.includes(tagId)) {
    activeEditorDiets = activeEditorDiets.filter((t) => t !== tagId);
  } else {
    activeEditorDiets.push(tagId);
  }
  renderDietChipGrid();
}

export function normalizeSubstituteReason(rawReason) {
  const r = (rawReason || "").toLowerCase().trim();
  if (r === "flavor" || r.includes("flavor")) return "flavor";
  if (r === "availability" || r.includes("pantry") || r.includes("availability") || r.includes("store")) return "availability";
  if (r === "dietary" || r.includes("diet") || r.includes("vegan") || r.includes("allergy")) return "dietary";
  return "flavor";
}

export function renderSubstituteCards() {
  const container = document.getElementById("formIngSubstitutesList");
  if (!container) return;

  if (activeEditorSubstitutes.length === 0) {
    container.innerHTML = `<p class="text-sm text-gray-500 italic dark:text-gray-400">No substitutes yet.</p>`;
    return;
  }

  container.innerHTML = activeEditorSubstitutes.map((sub) => {
    const catalogItem = activeMasterIngredients[sub.uid];
    const uid = escapeHtml(sub.uid);
    const reason = normalizeSubstituteReason(sub.reason);
    const option = (value, label) => `<option value="${value}" ${reason === value ? "selected" : ""}>${label}</option>`;
    const idLine = catalogItem
      ? `<span class="font-mono text-xs text-gray-500 dark:text-gray-400">${uid}</span>`
      : `<button type="button" onclick="window.crmApp.openAddMissingIngredientForm(${jsArg(sub.uid)})" class="inline-flex items-center gap-x-1 text-xs font-medium text-yellow-700 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-300">${solidIcon("exclamation-triangle", "size-4")}Not in the catalog: open a draft</button>`;

    return `
      <div class="flex items-center gap-3 rounded-md bg-gray-50 p-2 pl-3 dark:bg-white/5">
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-medium text-gray-900 dark:text-white">${escapeHtml(catalogItem?.display_name || sub.uid)}</div>
          ${idLine}
        </div>
        <div class="w-48 flex-none">
          <select aria-label="Why it substitutes" onchange="window.crmApp.updateSubstituteReason(${jsArg(sub.uid)}, this.value)" class="${FIELD.selectSm}">
            ${option("flavor", "Flavor: tastes alike")}${option("availability", "Availability: easier to find")}${option("dietary", "Dietary: for a diet or allergy")}
          </select>
        </div>
        <button type="button" title="Remove this substitute" onclick="window.crmApp.removeSubstitute(${jsArg(sub.uid)})" class="${BUTTON.iconDanger}"><span class="sr-only">Remove</span>${solidIcon("x-mark", "size-4")}</button>
      </div>
    `;
  }).join("");
}

// The substitute picker (an el-autocomplete): every catalog ingredient but this one and those already
// chosen, which it filters as you type, plus one option for an ID the catalog does not have.
const SUB_OPTION =
  "block px-3 py-2 text-gray-900 select-none aria-selected:bg-paprika-600 aria-selected:text-white dark:text-white dark:aria-selected:bg-paprika-500";

function substituteCandidates() {
  const currentKey = document.getElementById("formIngKey")?.value || "";
  const chosen = new Set(activeEditorSubstitutes.map((s) => s.uid));
  const nameOf = (k) => activeMasterIngredients[k]?.display_name || activeMasterIngredients[k]?.name || k;
  return Object.keys(activeMasterIngredients)
    .filter((k) => k !== currentKey && !chosen.has(k))
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b)))
    .map((k) => ({ uid: k, name: nameOf(k) }));
}

// el-autocomplete matches the typed text anywhere in an option's text, so the name and the ID keep a
// space between them: "Pine Nuts" + "pine_nuts" run together contain "spin".
export function renderSubstituteOptions() {
  const list = document.getElementById("formIngSubSearchOptions");
  if (!list) return;
  list.innerHTML = substituteCandidates().map(({ uid, name }) => `
    <el-option value="${escapeHtml(uid)}" class="${SUB_OPTION}">
      <div class="flex gap-x-2"><span class="truncate">${escapeHtml(name)}</span> <span class="truncate font-mono text-xs/6 text-gray-500 in-aria-selected:text-white/80 dark:text-gray-400">${escapeHtml(uid)}</span></div>
    </el-option>`).join("") + `<el-option id="formIngSubCustomOption" value="" class="${SUB_OPTION}"></el-option>`;
}

// The option for an ID the catalog lacks. el-autocomplete reads an option's text once but its value
// live, so the value is what was typed (it matches) and data-uid the ID it becomes.
export function onSubstituteSearchInput(query = "") {
  const custom = document.getElementById("formIngSubCustomOption");
  if (!custom) return;
  const uid = query.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
  const currentKey = document.getElementById("formIngKey")?.value || "";
  const offer = uid && !activeMasterIngredients[uid] && uid !== currentKey && !activeEditorSubstitutes.some((s) => s.uid === uid);
  custom.setAttribute("value", offer ? query : "");
  custom.dataset.uid = offer ? uid : "";
  custom.innerHTML = offer ? `<span class="truncate">Add <span class="font-mono">${escapeHtml(uid)}</span>, not in the catalog</span>` : "";
}

// el-autocomplete reports a pick as a change with the option's value; typing alone changes nothing.
export function onSubstituteChosen(value = "") {
  if (!value) return;
  if (activeMasterIngredients[value]) {
    addSubstitute(value);
    return;
  }
  const custom = document.getElementById("formIngSubCustomOption");
  if (custom?.dataset.uid && value === custom.getAttribute("value")) addSubstitute(custom.dataset.uid);
}

export function addSubstitute(uid) {
  if (!uid) return;
  const existing = activeEditorSubstitutes.find((s) => s.uid === uid);
  if (!existing) {
    activeEditorSubstitutes.push({
      uid: uid,
      reason: "flavor"
    });
  }

  const input = document.getElementById("formIngSubSearchInput");
  if (input) input.value = "";
  onSubstituteSearchInput("");
  renderSubstituteOptions();
  renderSubstituteCards();
}

export function removeSubstitute(uid) {
  activeEditorSubstitutes = activeEditorSubstitutes.filter((s) => s.uid !== uid);
  renderSubstituteOptions();
  renderSubstituteCards();
}

export function updateSubstituteReason(uid, newReason) {
  const item = activeEditorSubstitutes.find((s) => s.uid === uid);
  if (item) {
    item.reason = normalizeSubstituteReason(newReason);
  }
}

export function openAddMissingIngredientForm(missingUid) {
  if (!missingUid) return;

  // 1. Close the inspector (the editor reopens below)
  hideDialog("ingredientInspectorModal");

  // 2. Format a clean display name from missing UID (e.g. "bean_sprouts" -> "Bean Sprouts")
  const formattedName = missingUid
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  // 3. Open Create Master Ingredient editor modal
  openIngredientEditor(null, activeMasterIngredients, activeCategoriesData);

  // 4. Pre-fill form fields
  const uidInput = document.getElementById("formIngUid");
  const nameInput = document.getElementById("formIngName");

  if (uidInput) uidInput.value = missingUid;
  if (nameInput) nameInput.value = formattedName;

  if (typeof window !== "undefined" && window.crmApp && window.crmApp.showToast) {
    window.crmApp.showToast(`Opened a draft of '${formattedName}' (${missingUid}). Fill it with AI, then register it in recipe-builder.`, "info");
  }

  validateIngredientDuplicates();
}

export function validateIngredientDuplicates() {
  const currentKey = document.getElementById("formIngKey")?.value.trim() || "";
  const uidInput = document.getElementById("formIngUid");
  const nameInput = document.getElementById("formIngName");
  const uidFeedback = document.getElementById("formIngUidFeedback");
  const nameFeedback = document.getElementById("formIngNameFeedback");
  const saveBtn = document.getElementById("btnSaveIngredient");

  if (!uidInput || !nameInput) return;

  const rawUid = uidInput.value.trim();
  const normalizedUid = rawUid ? rawUid.toLowerCase().replace(/[^a-z0-9_]/g, "_") : "";
  const rawName = nameInput.value.trim().toLowerCase();

  let isUidDuplicate = false;
  let isNameDuplicate = false;
  let conflictingNameUid = "";
  let existingItemNameForUid = "";

  // 1. Validate UID Duplicate
  if (normalizedUid && (!currentKey || normalizedUid !== currentKey)) {
    const existingUidItem = activeMasterIngredients[normalizedUid];
    if (existingUidItem) {
      isUidDuplicate = true;
      existingItemNameForUid = existingUidItem.display_name || existingUidItem.name || normalizedUid;
    }
  }

  // 2. Validate Display Name Duplicate
  if (rawName) {
    const existingNameKey = Object.keys(activeMasterIngredients).find((k) => {
      if (k === currentKey) return false;
      const item = activeMasterIngredients[k];
      const itemName = (item.display_name || item.name || "").trim().toLowerCase();
      return itemName && itemName === rawName;
    });

    if (existingNameKey) {
      isNameDuplicate = true;
      conflictingNameUid = existingNameKey;
    }
  }

  // 3. Render UID feedback
  if (isUidDuplicate) {
    if (uidFeedback) {
      uidFeedback.style.display = "block";
      uidFeedback.textContent = `${existingItemNameForUid} already has the ID ${normalizedUid}.`;
    }
    uidInput.setAttribute("aria-invalid", "true");
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.style.opacity = "0.6";
      saveBtn.style.cursor = "not-allowed";
    }
  } else {
    if (uidFeedback) {
      uidFeedback.style.display = "none";
      uidFeedback.textContent = "";
    }
    uidInput.removeAttribute("aria-invalid");
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.style.opacity = "1";
      saveBtn.style.cursor = "pointer";
    }
  }

  // 4. Render Name feedback
  if (isNameDuplicate) {
    if (nameFeedback) {
      nameFeedback.style.display = "block";
      nameFeedback.textContent = `${conflictingNameUid} already has this name.`;
    }
    nameInput.toggleAttribute("data-duplicate", true);
  } else {
    if (nameFeedback) {
      nameFeedback.style.display = "none";
      nameFeedback.textContent = "";
    }
    nameInput.toggleAttribute("data-duplicate", false);
  }
}

const PHOTO_HINT = "An identification shot on a clean off-white background, stored in Cloudinary under ingredients/";

export function openIngredientEditor(key = null, masterIngredients = {}, categoriesData = {}) {
  if (!document.getElementById("ingredientModal")) return;

  const form = document.getElementById("ingredientForm");
  form.reset();

  const currentSubgroup = (key && masterIngredients[key])
    ? (masterIngredients[key].subgroup_code || masterIngredients[key].category || null)
    : null;

  activeCategoriesData = categoriesData;
  activeMasterIngredients = masterIngredients;
  populateCategoryDropdown(categoriesData, currentSubgroup);

  const uidInput = document.getElementById("formIngUid");

  if (key && masterIngredients[key]) {
    const item = masterIngredients[key];
    document.getElementById("ingredientModalTitle").textContent = item.display_name || key;
    document.getElementById("formIngKey").value = key;
    
    if (uidInput) {
      uidInput.value = key;
      uidInput.disabled = true;
    }

    document.getElementById("formIngName").value = item.display_name || item.name || key;
    if (currentSubgroup) {
      document.getElementById("formIngCategory").value = currentSubgroup;
    }
    const displayUnit = item.display_unit || "gram";
    const pluralUnit = item.plural || getPluralUnit(displayUnit);
    document.getElementById("formIngDisplayUnit").value = displayUnit;
    const pluralInput = document.getElementById("formIngPluralUnit");
    if (pluralInput) {
      pluralInput.value = pluralUnit;
    }
    document.getElementById("formIngCanonicalUnit").value = item.canonical_unit || "gram";
    const canonQtyInput = document.getElementById("formIngCanonicalQuantity");
    if (canonQtyInput) {
      canonQtyInput.value = item.canonical_quantity != null ? item.canonical_quantity : 1;
    }
    document.getElementById("formIngIsPantryStaple").checked = item.is_pantry_staple === true;
    document.getElementById("formIngExcludeFromGroceryList").checked = item.exclude_from_grocery_list === true;
    setPerishableCheckbox(item.is_perishable);
    setPackSizeInput(item.pack_size);
    setPriceTierSelect(item.price_tier);

    activeEditorAllergens = [...(item.allergen_flags || item.allergens || [])];
    activeEditorDiets = [...(item.dietary_preferences || [])];

    const rawSubs = item.substitutes || [];
    activeEditorSubstitutes = rawSubs.map((s) => {
      if (typeof s === "string") return { uid: s, reason: "flavor" };
      return { uid: s.uid || s.id || "", reason: normalizeSubstituteReason(s.reason) };
    }).filter((s) => Boolean(s.uid));

    // Photo initialization
    const imagePath = item.image_path || item.imageUrl || item.image_url || "";
    const imagePathInput = document.getElementById("formIngImagePath");
    if (imagePathInput) {
      imagePathInput.value = imagePath;
    }
    onIngredientImagePathChanged(imagePath);
    const costBadge = document.getElementById("formIngPhotoCostBadge");
    if (costBadge) {
      costBadge.style.display = "none";
      costBadge.textContent = "";
    }
    const photoStatus = document.getElementById("formIngPhotoStatus");
    if (photoStatus) {
      photoStatus.textContent = imagePath ? imagePath : PHOTO_HINT;
    }
  } else {
    document.getElementById("ingredientModalTitle").textContent = "New ingredient (draft)";
    document.getElementById("formIngKey").value = "";
    activeEditorAllergens = [];
    activeEditorDiets = [];
    activeEditorSubstitutes = [];
    const pluralInput = document.getElementById("formIngPluralUnit");
    if (pluralInput) {
      pluralInput.value = "grams";
    }
    const canonQtyInput = document.getElementById("formIngCanonicalQuantity");
    if (canonQtyInput) {
      canonQtyInput.value = 1;
    }
    document.getElementById("formIngCanonicalUnit").value = "gram";
    document.getElementById("formIngIsPantryStaple").checked = false;
    document.getElementById("formIngExcludeFromGroceryList").checked = false;
    setPerishableCheckbox(undefined);
    setPackSizeInput(undefined);
    setPriceTierSelect(undefined);
    if (uidInput) {
      uidInput.value = "";
      uidInput.disabled = false;
    }

    // Photo reset for new ingredient
    const imagePathInput = document.getElementById("formIngImagePath");
    if (imagePathInput) {
      imagePathInput.value = "";
    }
    onIngredientImagePathChanged("");
    const costBadge = document.getElementById("formIngPhotoCostBadge");
    if (costBadge) {
      costBadge.style.display = "none";
      costBadge.textContent = "";
    }
    const photoStatus = document.getElementById("formIngPhotoStatus");
    if (photoStatus) {
      photoStatus.textContent = PHOTO_HINT;
    }
  }

  renderAllergenChipGrid();
  renderDietChipGrid();
  renderSubstituteCards();
  renderSubstituteOptions();
  onSubstituteSearchInput("");
  validateIngredientDuplicates();

  const dispUnitSelect = document.getElementById("formIngDisplayUnit");
  if (dispUnitSelect && !dispUnitSelect.dataset.pluralListenerAttached) {
    dispUnitSelect.dataset.pluralListenerAttached = "true";
    dispUnitSelect.addEventListener("change", () => {
      const plInput = document.getElementById("formIngPluralUnit");
      if (plInput) {
        plInput.value = getPluralUnit(dispUnitSelect.value);
      }
    });
  }

  showDialog("ingredientModal");
}

export async function saveMasterIngredient(db, masterIngredients, setDoc, doc, showToast, dismissToast, renderIngredientsTable) {
  if (refuseCatalogWrite(showToast)) return; // git owns the catalog (#1188)
  const isEditing = Boolean(document.getElementById("formIngKey").value.trim());
  let key = document.getElementById("formIngKey").value.trim();
  
  if (!isEditing) {
    const rawUid = document.getElementById("formIngUid")?.value.trim();
    const rawName = document.getElementById("formIngName")?.value.trim();
    key = (rawUid || rawName).toLowerCase().replace(/[^a-z0-9_]/g, "_");
  }

  const displayName = document.getElementById("formIngName").value.trim();
  if (!key || !displayName) {
    showToast("Key and Display Name are required.", "danger");
    return;
  }

  const categorySelect = document.getElementById("formIngCategory");
  const categoryCode = categorySelect?.value || "pantry";
  const categoryName = (categorySelect?.options && categorySelect.selectedIndex >= 0 && categorySelect.options[categorySelect.selectedIndex]?.text)
    ? categorySelect.options[categorySelect.selectedIndex].text
    : categoryCode.replace(/_/g, " ");
  const displayUnit = document.getElementById("formIngDisplayUnit").value;
  const pluralUnit = document.getElementById("formIngPluralUnit")?.value.trim() || getPluralUnit(displayUnit);
  const canonicalUnit = document.getElementById("formIngCanonicalUnit").value;
  const canonicalQuantity = Number(document.getElementById("formIngCanonicalQuantity")?.value || 1);
  const isPantryStaple = document.getElementById("formIngIsPantryStaple").checked;
  const excludeFromGroceryList = document.getElementById("formIngExcludeFromGroceryList").checked;
  const isPerishable = readPerishableCheckbox();
  const packSize = readPackSizeInput();
  const priceTier = readPriceTierSelect();
  const imagePath = document.getElementById("formIngImagePath")?.value.trim() || null;

  const allergens = [...activeEditorAllergens];
  const diets = [...activeEditorDiets];
  const substitutes = activeEditorSubstitutes.map((s) => ({
    uid: s.uid,
    reason: normalizeSubstituteReason(s.reason)
  }));

  const ingredientPayload = {
    uid: key,
    display_name: displayName,
    display_unit: displayUnit,
    plural: pluralUnit,
    canonical_unit: canonicalUnit,
    canonical_quantity: canonicalQuantity,
    is_pantry_staple: isPantryStaple,
    ...(excludeFromGroceryList ? { exclude_from_grocery_list: true } : {}),
    ...(typeof isPerishable === "boolean" ? { is_perishable: isPerishable } : {}),
    ...(typeof packSize === "number" ? { pack_size: packSize } : {}),
    ...(priceTier ? { price_tier: priceTier } : {}),
    subgroup_code: categoryCode,
    subgroup_name: categoryName,
    allergen_flags: allergens,
    dietary_preferences: diets,
    substitutes: substitutes,
    ...(imagePath ? { image_path: imagePath } : {}),
    updated_at: new Date().toISOString()
  };

  masterIngredients[key] = ingredientPayload;
  const processingToast = showToast(`⏳ Saving ingredient '${key}' to Firestore...`, "processing");

  const saveBtn = document.getElementById("btnSaveIngredient");
  const originalBtnHtml = saveBtn ? saveBtn.innerHTML : "Save Master Ingredient";
  if (saveBtn) {
    saveBtn.disabled = true;
    saveBtn.style.opacity = "0.7";
    saveBtn.style.cursor = "not-allowed";
    saveBtn.innerHTML = `⏳ Saving...`;
  }

  try {
    await setDoc(doc(db, "globals", "master_ingredients"), {
      ingredients: masterIngredients,
      count: Object.keys(masterIngredients).length,
      image_url_prefix: activeMasterIngredientsPrefix || "https://res.cloudinary.com/psttnxd0/image/upload/f_auto,q_auto",
      updated_at: new Date().toISOString()
    });

    dismissToast(processingToast);
    hideDialog("ingredientModal");
    if (renderIngredientsTable) renderIngredientsTable();
    showToast(`Saved master ingredient '${key}'!`, "success");
  } catch (err) {
    dismissToast(processingToast);
    console.error("❌ Save Ingredient Error:", err);
    showToast("❌ Failed to save ingredient: " + err.message, "danger");
  } finally {
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.style.opacity = "1";
      saveBtn.style.cursor = "pointer";
      saveBtn.innerHTML = originalBtnHtml;
    }
  }
}

export async function deleteMasterIngredient(key, db, masterIngredients, setDoc, doc, showToast, dismissToast, renderIngredientsTable) {
  if (refuseCatalogWrite(showToast)) return; // git owns the catalog (#1188)
  if (!confirm(`Delete master ingredient '${key}'?`)) return;
  delete masterIngredients[key];
  const processingToast = showToast(`⏳ Deleting ingredient '${key}'...`, "processing");
  try {
    await setDoc(doc(db, "globals", "master_ingredients"), {
      ingredients: masterIngredients,
      count: Object.keys(masterIngredients).length,
      image_url_prefix: activeMasterIngredientsPrefix || DEFAULT_MASTER_INGREDIENTS_PREFIX,
      updated_at: new Date().toISOString()
    });
    dismissToast(processingToast);
    if (renderIngredientsTable) renderIngredientsTable();
    showToast(`Deleted ingredient '${key}'.`, "success");
  } catch (err) {
    dismissToast(processingToast);
    console.error("❌ Delete Ingredient Error:", err);
    showToast("❌ Failed to delete ingredient: " + err.message, "danger");
  }
}

export async function autofillIngredientWithAI(ai, getGenerativeModel, showToast, dismissToast, appCheck = null, getToken = null, SchemaType = null, categoriesData = null, masterIngredients = null) {
  if (categoriesData && Object.keys(categoriesData).length > 0) {
    activeCategoriesData = categoriesData;
  }
  if (masterIngredients && Object.keys(masterIngredients).length > 0) {
    activeMasterIngredients = masterIngredients;
  }

  const nameInput = document.getElementById("formIngName");
  const uidInput = document.getElementById("formIngUid");
  const rawName = nameInput ? nameInput.value.trim() : "";
  const rawUid = uidInput ? uidInput.value.trim() : "";
  const ingredientName = rawName || rawUid;

  if (!ingredientName) {
    showToast("Please enter an Ingredient Display Name or ID first to auto-fill with AI.", "warning");
    if (nameInput) nameInput.focus();
    return;
  }

  const statusEl = document.getElementById("aiAutoFillStatus");
  const btnEl = document.getElementById("btnAiAutoFill");

  if (statusEl) statusEl.style.display = "block";
  if (btnEl) btnEl.disabled = true;

  const processingToast = showToast(`✨ Asking Firebase Gemini AI to classify '${ingredientName}'...`, "processing");

  try {
    // Ensure App Check token is fresh and active before invoking Firebase AI
    if (appCheck && typeof getToken === "function") {
      try {
        const tokenRes = await getToken(appCheck, true);
        console.log("🛡️ [AppCheck] Successfully fetched fresh AppCheck token:", tokenRes ? tokenRes.token.substring(0, 12) + "..." : "ok");
      } catch (appCheckErr) {
        console.warn("⚠️ [AppCheck] Token refresh before AI call warning:", appCheckErr.message);
      }
    }

    // 1. Extract dynamic taxonomy lists from activeCategoriesData
    const ingCategories = activeCategoriesData.ingredient_categories || [];
    const subgroupCodes = [];
    ingCategories.forEach((cat) => {
      (cat.subgroups || []).forEach((sub) => {
        const code = typeof sub === "string" ? sub : sub.code || sub.id;
        if (code && !subgroupCodes.includes(code)) subgroupCodes.push(code);
      });
    });

    const allergenIds = (activeCategoriesData.allergens || []).map((a) => a.id).filter(Boolean);
    const dietaryIds = (activeCategoriesData.dietary_preferences || []).map((d) => d.id).filter(Boolean);
    const displayUnits = ["gram", "ml", "tbsp", "tsp", "cup", "clove", "can", "count", "block", "bulb", "bunch", "chopped", "fillet", "head", "link", "package", "patty", "pepper", "pinch", "scoop", "sheet", "slice", "spear", "sprig", "stalk", "steak", "whole", "heart"];

    // STRICT CHECK: Fail fast if any required database taxonomy list is unavailable
    if (!subgroupCodes.length) {
      throw new Error("Failed to load category subgroup codes from database taxonomy (/globals/categories).");
    }
    if (!allergenIds.length) {
      throw new Error("Failed to load allergen IDs from database taxonomy (/globals/categories).");
    }
    if (!dietaryIds.length) {
      throw new Error("Failed to load dietary preference IDs from database taxonomy (/globals/categories).");
    }

    const promptText = `You are a professional culinary food safety and ingredient classification expert.
Your task is to analyze the given ingredient display name and select its exact Subcategory Code, UI Display Unit, Plural Unit (e.g. cloves, cups, tbsp, grams, pkgs), Canonical Base Unit (strictly "gram" or "ml"), Canonical Quantity (exact number of grams or ml in 1 display unit), Pantry Staple Status, a Price Tier ("$", "$$", "$$$", or "$$$$" — the current US grocery price for roughly 400g or 400ml of this ingredient as sold: $ is about $2, $$ about $5, $$$ about $9, $$$$ about $15 or more; compare to similar ingredients you already know the tier of), detected Allergen IDs, compatible Dietary Preference IDs, and 1 to 3 realistic ingredient substitutes constrained strictly by the response schema.

### INGREDIENT DISPLAY NAME:
${ingredientName}`;

    // 2. Define formal OpenAPI responseSchema object with database-persisted enum constraints
    const ingredientSchema = SchemaType ? {
      type: SchemaType.OBJECT,
      properties: {
        display_name: { type: SchemaType.STRING },
        subgroup_code: { 
          type: SchemaType.STRING,
          enum: subgroupCodes
        },
        display_unit: { 
          type: SchemaType.STRING,
          enum: displayUnits
        },
        plural: {
          type: SchemaType.STRING
        },
        canonical_unit: { 
          type: SchemaType.STRING,
          enum: ["gram", "ml"]
        },
        canonical_quantity: { type: SchemaType.NUMBER },
        is_pantry_staple: { type: SchemaType.BOOLEAN },
        is_perishable: { type: SchemaType.BOOLEAN },
        price_tier: {
          type: SchemaType.STRING,
          enum: PRICE_TIERS
        },
        allergen_flags: {
          type: SchemaType.ARRAY,
          items: { 
            type: SchemaType.STRING,
            enum: allergenIds
          }
        },
        dietary_preferences: {
          type: SchemaType.ARRAY,
          items: { 
            type: SchemaType.STRING,
            enum: dietaryIds
          }
        },
        suggested_substitutes: {
          type: SchemaType.ARRAY,
          items: {
            type: SchemaType.OBJECT,
            properties: {
              uid: { type: SchemaType.STRING },
              reason: { 
                type: SchemaType.STRING,
                enum: ["flavor", "availability", "dietary"]
              }
            },
            required: ["uid", "reason"]
          }
        }
      },
      required: [
        "display_name",
        "subgroup_code",
        "display_unit",
        "plural",
        "canonical_unit",
        "canonical_quantity",
        "is_pantry_staple",
        "allergen_flags",
        "dietary_preferences",
        "suggested_substitutes"
      ]
    } : null;

    // 3. Create GenerativeModel instance with responseSchema
    const modelOptions = {
      model: "gemini-3.5-flash-lite",
      generationConfig: {
        responseMimeType: "application/json"
      }
    };

    if (ingredientSchema) {
      modelOptions.generationConfig.responseSchema = ingredientSchema;
    }

    const model = getGenerativeModel(ai, modelOptions);

    // 4. Generate content
    const result = await model.generateContent(promptText);

    const responseText = result.response.text();
    console.log("✨ [AI Response Raw Text]:", responseText);
    const aiData = JSON.parse(responseText);
    console.log("✨ [AI Response Parsed JSON]:", aiData);

    // 5. Populate Form Fields On Screen
    if (aiData.display_name && nameInput && !nameInput.value.trim()) {
      nameInput.value = aiData.display_name;
    }

    if (uidInput && !uidInput.disabled && (!uidInput.value.trim() || uidInput.value.trim() === ingredientName)) {
      const generatedUid = (aiData.display_name || ingredientName).toLowerCase().replace(/[^a-z0-9_]/g, "_");
      uidInput.value = generatedUid;
    }

    if (aiData.subgroup_code) {
      const catSelect = document.getElementById("formIngCategory");
      if (catSelect) catSelect.value = aiData.subgroup_code;
    }

    if (aiData.display_unit) {
      const dispUnitSelect = document.getElementById("formIngDisplayUnit");
      if (dispUnitSelect) dispUnitSelect.value = aiData.display_unit;
      const pluralInput = document.getElementById("formIngPluralUnit");
      if (pluralInput) pluralInput.value = getPluralUnit(aiData.display_unit);
    }

    if (aiData.canonical_unit) {
      const canonUnitSelect = document.getElementById("formIngCanonicalUnit");
      if (canonUnitSelect) canonUnitSelect.value = aiData.canonical_unit === "ml" ? "ml" : "gram";
    }

    if (typeof aiData.canonical_quantity === "number") {
      const canonQtyInput = document.getElementById("formIngCanonicalQuantity");
      if (canonQtyInput) canonQtyInput.value = aiData.canonical_quantity;
    }

    if (typeof aiData.is_perishable === "boolean") setPerishableCheckbox(aiData.is_perishable);

    if (typeof aiData.is_pantry_staple === "boolean") {
      const pantryCheck = document.getElementById("formIngIsPantryStaple");
      if (pantryCheck) pantryCheck.checked = aiData.is_pantry_staple;
    }

    if (PRICE_TIERS.includes(aiData.price_tier)) setPriceTierSelect(aiData.price_tier);

    // Allergens
    if (Array.isArray(aiData.allergen_flags)) {
      activeEditorAllergens = aiData.allergen_flags.filter((algId) => allergenIds.includes(algId) || allergenIds.length === 0);
      renderAllergenChipGrid();
    }

    // Dietary preferences
    if (Array.isArray(aiData.dietary_preferences)) {
      activeEditorDiets = aiData.dietary_preferences.filter((dietId) => dietaryIds.includes(dietId) || dietaryIds.length === 0);
      renderDietChipGrid();
    }

    // Substitutes (Strict schema adherence: s.uid and s.reason)
    if (Array.isArray(aiData.suggested_substitutes)) {
      activeEditorSubstitutes = aiData.suggested_substitutes.map((s) => ({
        uid: s.uid,
        reason: normalizeSubstituteReason(s.reason)
      })).filter((s) => Boolean(s.uid));
      console.log("✨ [Populated Substitutes]:", activeEditorSubstitutes);
      renderSubstituteCards();
      renderSubstituteOptions();
    }

    validateIngredientDuplicates();
    dismissToast(processingToast);
    showToast(`✨ Filled in '${ingredientName}' with AI. Review the values.`, "success");
  } catch (err) {
    dismissToast(processingToast);
    console.error("❌ AI Auto-Fill Error:", err);
    showToast("❌ AI Auto-Fill failed: " + err.message, "danger");
  } finally {
    if (statusEl) statusEl.style.display = "none";
    if (btnEl) btnEl.disabled = false;
  }
}

export function copyRawIngredient(masterIngredients = {}, showToast) {
  const key = document.getElementById("formIngKey")?.value.trim();
  if (key && masterIngredients[key]) {
    navigator.clipboard.writeText(JSON.stringify(masterIngredients[key], null, 2));
    if (showToast) showToast("Copied JSON to clipboard!", "success");
  }
}

export async function generateAiPhotoForIngredient(
  ai,
  getGenerativeModel,
  ResponseModality,
  functions,
  httpsCallable,
  showToast,
  dismissToast,
  appCheck = null,
  getToken = null
) {
  const nameInput = document.getElementById("formIngName");
  const uidInput = document.getElementById("formIngUid");
  const keyInput = document.getElementById("formIngKey");
  const categorySelect = document.getElementById("formIngCategory");

  const displayName = nameInput?.value.trim() || "";
  const key = keyInput?.value.trim() || uidInput?.value.trim() || displayName.toLowerCase().replace(/[^a-z0-9_]/g, "_");
  const category = categorySelect?.value || "";

  if (!displayName) {
    if (showToast) showToast("Please enter an Ingredient Display Name before generating a photo.", "warning");
    if (nameInput) nameInput.focus();
    return;
  }

  const btn = document.getElementById("btnGenerateIngredientPhoto");
  const originalBtnText = btn ? btn.innerHTML : "Generate with AI";
  const photoStatus = document.getElementById("formIngPhotoStatus");

  if (btn) {
    btn.textContent = "Generating…";
    btn.disabled = true;
  }
  if (photoStatus) photoStatus.textContent = "Generating the photo with Gemini…";

  let processingToast = null;
  if (showToast) processingToast = showToast(`📸 Generating AI photo for '${displayName}'...`, "processing");

  try {
    // Refresh AppCheck token if needed
    if (appCheck && typeof getToken === "function") {
      try {
        await getToken(appCheck, true);
      } catch (appCheckErr) {
        console.warn("⚠️ AppCheck refresh warning:", appCheckErr.message);
      }
    }

    // 1. Build prompt using category-aware guidance
    const { promptText, stateGuidance } = buildIngredientPhotoPrompt(displayName, category);

    // 2. Configure model for image output
    const modelOptions = { model: "gemini-3.1-flash-image" };
    if (ResponseModality && ResponseModality.IMAGE) {
      modelOptions.generationConfig = {
        responseModalities: [ResponseModality.IMAGE]
      };
    }

    const model = getGenerativeModel(ai, modelOptions);
    const result = await model.generateContent(promptText);

    // 3. Extract base64 image data
    let base64Image = "";
    try {
      const inlineDataParts = result.response?.inlineDataParts?.() || [];
      if (inlineDataParts?.[0]) {
        const image = inlineDataParts[0].inlineData;
        base64Image = image?.data || image;
      }
    } catch (err) {
      console.warn("⚠️ inlineDataParts extraction warning:", err.message);
    }

    if (!base64Image && result.response?.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data) {
      base64Image = result.response.candidates[0].content.parts[0].inlineData.data;
    }

    if (!base64Image) {
      throw new Error("Gemini AI failed to return an image for this ingredient.");
    }

    // 4. Calculate generation cost from usageMetadata
    const usage = result.response?.usageMetadata || {};
    const promptTokens = usage.promptTokenCount || 0;
    const candidateTokens = usage.candidatesTokenCount || 0;
    const totalTokens = usage.totalTokenCount || (promptTokens + candidateTokens);
    const promptCost = (promptTokens / 1_000_000) * 0.10;
    const totalCost = promptCost + 0.039;
    const summaryCostText = `~$${totalCost.toFixed(3)} (${totalTokens} tokens)`;

    // 5. Upload to Cloudinary with folder: "ingredients"
    const uploadFn = httpsCallable(functions, "uploadToCloudinary");
    const uploadRes = await uploadFn({
      imageBase64: base64Image,
      ingredientId: key,
      folder: "ingredients",
      usageMetadata: usage
    });

    const publicId = uploadRes.data?.public_id;
    const imageUrl = uploadRes.data?.image_url;
    const thumbnailUrl = uploadRes.data?.thumbnail_url || imageUrl;

    if (!publicId && !imageUrl) {
      throw new Error("Cloudinary upload failed to return image asset.");
    }

    // Store public_id as the relative path
    const storedPath = publicId || imageUrl;

    const pathInput = document.getElementById("formIngImagePath");
    if (pathInput) {
      pathInput.value = storedPath;
    }
    onIngredientImagePathChanged(storedPath);

    const costBadge = document.getElementById("formIngPhotoCostBadge");
    if (costBadge) {
      costBadge.textContent = summaryCostText;
      costBadge.style.display = "inline-block";
    }

    if (photoStatus) {
      photoStatus.textContent = `Uploaded: ${storedPath}`;
    }

    if (dismissToast && processingToast) dismissToast(processingToast);
    if (showToast) showToast(`✅ Ingredient photo generated & uploaded! (${summaryCostText})`, "success");

  } catch (err) {
    if (dismissToast && processingToast) dismissToast(processingToast);
    console.error("❌ Ingredient Photo Generation Error:", err);
    if (photoStatus) photoStatus.textContent = `Generation failed: ${err.message}`;
    if (showToast) showToast(`❌ Photo generation failed: ${err.message}`, "danger");
  } finally {
    if (btn) {
      btn.innerHTML = originalBtnText;
      btn.disabled = false;
    }
  }
}

